import { useTranslation } from 'core/i18n/i18n';
import { showErrorNotification } from 'core/utils/notification-service';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getFileExtension } from '../settings-devices-eap-settings-importing/settings-devices-eap-settings-importing.business';

export const FILE_TYPES = ['.csv', '.prn', '.txt'];

type UploadStatus = 'idle' | 'uploading' | 'uploaded';

export const useImportModalFileUpload = () => {
    const { t } = useTranslation();

    const [filename, setFilename] = useState<string>('');
    const [fileContent, setFileContent] = useState<string>();
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
    const uploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isValidFile = useMemo<boolean>(() => FILE_TYPES.includes(getFileExtension(filename)), [filename]);

    const filePositiveAlert: 'positive' | 'alarm' = isValidFile ? 'positive' : 'alarm';
    const fileStatus: 'default' | 'positive' | 'alarm' = uploadStatus === 'uploading' ? 'default' : filePositiveAlert;

    const clearUploadSimulation = () => {
        if (uploadTimerRef.current) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
    };

    useEffect(() => () => clearUploadSimulation(), []);

    const onResetFile = () => {
        clearUploadSimulation();
        setFilename('');
        setFileContent(undefined);
        setUploadProgress(0);
        setUploadStatus('idle');
    };

    const onLoadFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files?.length) {
            return;
        }

        if (files.length > 1) {
            showErrorNotification(
                t('settings.devices.eap-settings.eap-config-list.action.import.modal.notification.only-one-file')
            );
            return;
        }

        const file = files[0];
        const fileExtension = getFileExtension(file.name);

        if (!FILE_TYPES.includes(fileExtension)) {
            setFilename(file.name);
            setUploadStatus('uploaded');
            return;
        }

        setFilename(file.name);
        setFileContent(undefined);
        setUploadStatus('uploading');
        setUploadProgress(0);

        file.text()
            .then((content) => {
                setUploadProgress(100);
                setFileContent(content);
                setUploadStatus('uploaded');
            })
            .catch(() => {
                onResetFile();
                showErrorNotification(
                    t('settings.devices.eap-settings.eap-config-list.action.import.modal.notification.upload-error')
                );
            });
    };

    const getUploadStatusText = () => {
        if (uploadStatus !== 'uploaded') {
            return `${uploadProgress}%`;
        }

        if (isValidFile) {
            return t('settings.devices.eap-settings.eap-config-list.action.import.modal.upload.status.uploaded');
        }

        return t('settings.devices.eap-settings.eap-config-list.action.import.modal.upload.status.invalid');
    };

    return {
        filename,
        fileContent,
        uploadProgress,
        uploadStatus,
        isValidFile,
        fileStatus,
        fileTypes: FILE_TYPES,
        onResetFile,
        onLoadFile,
        getUploadStatusText,
    };
};
